import { useMutation, useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState, useRef, useEffect } from 'react';
import { ReactSortable } from 'react-sortablejs';
import styled, { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import DateTimePicker from 'components/DateTimePicker';
import { MultiSelect, SearchableSelect } from 'components/FormFields';
import AttachmentUpload from 'components/FormFields/AttachmentUpload';
import ColorSelect from 'components/FormFields/ColorSelect';
import CreatableSelect from 'components/FormFields/CreatableSelect';
import Input from 'components/FormFields/Input';
import Select from 'components/FormFields/Select';
import Textarea from 'components/FormFields/Textarea';
import Toggle from 'components/FormFields/Toggle';
import Loading from 'components/Loading';
import {
  PageWrapper,
  Container,
  PageHeader,
  HeaderLeft,
  HeaderRight,
  BackButtonWrapper,
  Title,
  Subtitle,
  HeaderButtonWrapper,
  StepperContainer,
  StepperTrack,
  StepItem,
  StepCircle,
  StepLabel,
  StepConnector,
  StepNavigation,
  NavButtonWrapper,
  TwoColumnGrid,
  FormCard,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormRow,
  FormGroup,
  Label,
  LabelWithTooltip,
  ToggleRow,
  ToggleCard,
  ToggleInfo,
  ToggleTitle,
  ToggleDescription,
  SalesWindowsContainer,
  SalesWindowCard,
  SalesWindowHeader,
  SalesWindowTitle,
  SmallIconButtonWrapper,
  AddWindowButtonWrapper,
  EmptyWindowsMessage,
  TierSection,
  TierHeader,
  TierTitle,
  AddTierButtonWrapper,
  TierRow,
  EmptyTiersMessage,
  RemoveTierButtonWrapper,
  ArrowLeftIcon,
  PlusIcon,
  XIcon,
  ArrowRightIcon,
  CheckIcon,
  MinusIcon,
  TrashIcon,
  ANNUAL_TICKET_TYPES,
  VISIBILITY_OPTIONS,
  PagesContainer,
  PagesListSection,
  PagesOrderSection,
  PageCard,
  PageCardHeader,
  PageCardTitle,
  DeletePageButtonWrapper,
  AddPageButtonWrapper,
  EmptyPagesMessage,
  OrderSectionTitle,
  DraggablePageItem,
  DragHandle,
  DragHandleIcon,
  PageOrderName,
  WaiversContainer,
  WaiverRow,
  RemoveWaiverButtonWrapper,
  AddWaiverButtonWrapper,
  EmptyText,
  ErrorText,
} from 'components/TicketForms';
import { useFormstackSearch } from 'components/TicketForms/hooks/useFormstackSearch';
import Toaster from 'components/Toaster';
import Tooltip from 'components/Tooltip';
import { useGetAnnualTypesByAccountAdmin } from 'pages/app/AnnualTickets/AddAnnualTickets/gql/GetAnnualTypesByAccountAdmin';
import { COLOR_CODES } from 'pages/app/Events/EventEdit/EditEventTickets';
import { GET_WAIVERS } from 'pages/app/Events/EventEdit/WaiversRepeater/Waivers.queries';

// GraphQL queries
const GET_TRACKS = gql`
  query GetTracks($input: GetTracksInput!) {
    getTracks(input: $input) {
      id
      name
    }
  }
`;

const GET_SERIES = gql`
  query GetSeries($input: GetSeriesInput!) {
    getSeries(input: $input) {
      id
      name
    }
  }
`;

const GET_EVENTS_BY_DATE_RANGE = gql`
  query GetEventsByDateRange($input: GetEventsByDateRangeInput!) {
    getEventsByDateRange(input: $input) {
      id
      name
      start_date
    }
  }
`;

// Mutation to create a new annual type/category
const CREATE_ANNUAL_TYPE = gql`
  mutation CreateAnnualType($input: CreateAnnualTypeInput!) {
    createAnnualType(input: $input) {
      id
      name
    }
  }
`;

// Styled components for events list (Option 5: Minimal List with Date Badge)
const EventsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 350px;
  overflow-y: auto;
  padding: 4px;
`;

const EventItem = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  background: ${({ $selected, theme }) =>
    $selected
      ? theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.04)'
        : 'rgba(0, 0, 0, 0.03)'
      : 'transparent'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.04)'
        : 'rgba(0, 0, 0, 0.03)'};
  }
`;

const EventCheckbox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid
    ${({ $checked, theme }) =>
      $checked
        ? theme.colors.primary
        : theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(0, 0, 0, 0.25)'};
  background: ${({ $checked, theme }) =>
    $checked ? theme.colors.primary : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.15s;
`;

const EventCheckIcon = styled.span`
  color: white;
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
`;

const EventDateBadge = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.06)'};
  border-radius: 20px;
  min-width: 120px;
  justify-content: center;
`;

const EventDateText = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#1e293b')};
  letter-spacing: -0.3px;
`;

const EventName = styled.span`
  flex: 1;
  font-size: 14px;
  color: ${({ $selected, theme }) =>
    $selected
      ? theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.7)'
        : 'rgba(0, 0, 0, 0.7)'
      : theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.5)'
      : 'rgba(0, 0, 0, 0.5)'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray?.muted || '#64748b'};
  text-align: center;
  padding: 24px;
  margin: 0;
`;

const SelectAllRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)'};
  border-radius: 8px;
  margin-bottom: 12px;
`;

const SelectAllLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text?.primary || '#1e293b'};
`;

const SelectButton = styled.button`
  background: none;
  border: none;
  color: #fa4616;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
`;

// Step configuration
const steps = [
  { number: 1, label: 'Basic Info' },
  { number: 2, label: 'Pricing' },
  { number: 3, label: 'Associations' },
  { number: 4, label: 'Options' },
  { number: 5, label: 'Pages' },
];

// Default form data
const getDefaultFormData = () => ({
  // Basic info
  name: '',
  ticket_type: 'annual',
  description: '',
  description_image_id: null,
  description_image_url: null,
  description_video: '',
  color_code: '',
  // Validity dates
  start_date: null,
  end_date: null,
  // Sale dates
  sale_start: null,
  on_sale_time: '',
  sale_end: null,
  off_sale_time: '',
  // Toggles for step 1
  reserved_seat: false,
  has_sales_windows: false,
  // Reserved seat options
  reserved_seat_category: '',
  reserved_seat_prefix: '',
  reserved_seat_section: false,
  // Pricing
  price: '',
  track_price: '',
  fee_dollar: '',
  fee_percent: '',
  // Fee options (annual-specific)
  show_fee: true,
  international_fee: true,
  marketing_fee: true,
  // Tiers and windows
  tiers: [],
  windows: [],
  // Associations
  track_ids: [],
  series_ids: [],
  event_ids: [],
  for_all_events: true,
  generate_passes: false,
  display_events: false,
  display_track_series: false,
  // Options
  visibility: 'visible',
  category_id: '',
  limit: '',
  low_ticket: '',
  is_featured: false,
  featured_text: '',
  // Member numbers (annual-specific)
  set_member_number: false,
  prefix_text: '',
  suffix_text: '',
  last_member_number: '',
  // Pages
  pages: [],
  pages_with_order: [],
  // Status
  status: 'draft',
});

/**
 * AnnualTicketForm - Shared form component for Add and Edit Annual Ticket flows
 *
 * @param {string} mode - 'add' or 'edit'
 * @param {object} initialData - Initial ticket data (null for add, ticket object for edit)
 * @param {function} onSubmit - Callback when form is submitted (receives form data)
 * @param {function} onCancel - Callback when cancel/back is clicked
 * @param {boolean} saving - Whether the form is currently saving
 */
const AnnualTicketForm = ({
  mode = 'add',
  initialData = null,
  onSubmit,
  onCancel,
  saving = false,
  accountId = null,
}) => {
  const inputRef = useRef(null);
  const theme = useTheme();
  const infoIconColor =
    theme?.colors?.textGray?.dark ||
    (theme?.name === 'dark' ? '#e2e8f0' : '#64748b');

  const isEditMode = mode === 'edit';

  // Fetch annual types/categories for the account
  const { data: annualTypesData } = useGetAnnualTypesByAccountAdmin(accountId);
  const annualTypes =
    annualTypesData?.getAnnualTypesByAccountAdmin?.results || [];
  const categoryOptions = annualTypes.map((type) => ({
    value: type.id,
    label: type.name,
  }));

  // Fetch waivers for pages
  const { data: waiversData, loading: waiversLoading } = useQuery(GET_WAIVERS, {
    fetchPolicy: 'cache-and-network',
  });
  const waivers = waiversData?.getWaivers || [];

  // Fetch Formstack forms for pages
  const {
    options: formstackOptions,
    loading: formstackLoading,
    handleSearch: handleFormstackSearch,
  } = useFormstackSearch(4, {
    prefetch: (initialData?.pages || []).some((p) => p.form_id),
  });

  // Mutation to create new annual type/category
  const [createAnnualType] = useMutation(CREATE_ANNUAL_TYPE, {
    refetchQueries: ['GetAnnualTypesByAccountAdmin'],
  });

  // Handle creating a new category
  const handleCreateCategory = async (categoryName) => {
    if (!accountId) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Account ID is required to create a category',
      });
      throw new Error('Account ID is required');
    }

    try {
      const { data } = await createAnnualType({
        variables: {
          input: {
            name: categoryName,
            account_id: parseInt(accountId),
            ...(formData.ticket_type ? { type: formData.ticket_type } : {}),
          },
        },
      });
      const newCategory = data?.createAnnualType;
      if (newCategory) {
        setToaster({
          isOpen: true,
          type: 'success',
          title: 'Category Created',
          message: `"${newCategory.name}" has been created successfully.`,
        });
        return { value: newCategory.id, label: newCategory.name };
      }
      throw new Error('Failed to create category');
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to create category',
      });
      throw error;
    }
  };

  // Fetch tracks
  const { data: tracksData, loading: tracksLoading } = useQuery(GET_TRACKS, {
    variables: { input: {} },
  });
  const tracks = tracksData?.getTracks || [];

  // Fetch series
  const { data: seriesData, loading: seriesLoading } = useQuery(GET_SERIES, {
    variables: { input: {} },
  });
  const series = seriesData?.getSeries || [];

  const [formData, setFormData] = useState(getDefaultFormData());
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [toaster, setToaster] = useState({
    isOpen: false,
    type: '',
    title: '',
    message: '',
  });
  const [dataLoaded, setDataLoaded] = useState(false);

  // Populate form with initial data (for edit mode)
  useEffect(() => {
    if (initialData && !dataLoaded) {
      setFormData({
        name: initialData.name || '',
        ticket_type: initialData.ticket_type || 'annual',
        description: initialData.description || '',
        description_image_id: initialData.description_image_id || null,
        description_image_url: initialData.description_image_url || null,
        description_video: initialData.description_video || '',
        color_code: initialData.color_code || '',
        start_date: initialData.start_date || null,
        end_date: initialData.end_date || null,
        sale_start: initialData.sale_start || null,
        on_sale_time: initialData.on_sale_time || '',
        sale_end: initialData.sale_end || null,
        off_sale_time: initialData.off_sale_time || '',
        reserved_seat: initialData.reserved_seat || false,
        reserved_seat_category: initialData.reserved_seat_category || '',
        reserved_seat_prefix: initialData.reserved_seat_prefix || '',
        reserved_seat_section: initialData.reserved_seat_section || false,
        price:
          initialData.price !== null && initialData.price !== undefined
            ? String(initialData.price)
            : '',
        track_price:
          initialData.track_price !== null &&
          initialData.track_price !== undefined
            ? String(initialData.track_price)
            : '',
        fee_dollar:
          initialData.fee_dollar !== null &&
          initialData.fee_dollar !== undefined
            ? String(initialData.fee_dollar)
            : '',
        fee_percent:
          initialData.fee_percent !== null &&
          initialData.fee_percent !== undefined
            ? String(initialData.fee_percent)
            : '',
        show_fee: initialData.show_fee !== false,
        international_fee: initialData.international_fee || false,
        marketing_fee: initialData.marketing_fee || false,
        tiers: [], // TODO: populate from initialData if needed
        windows: (initialData.windows || []).map((w) => ({
          id: w.id,
          name: w.name || '',
          ends: w.ends || '',
          // Strip seconds from time if present (API returns "23:59:00", DateTimePicker expects "23:59")
          time: w.time ? w.time.substring(0, 5) : '23:59',
          price:
            w.price !== null && w.price !== undefined ? String(w.price) : '',
          tiers: (w.tiers || []).map((t) => ({
            id: t.id,
            name: t.name || '',
            price:
              t.price !== null && t.price !== undefined ? String(t.price) : '',
            track_price:
              t.track_price !== null && t.track_price !== undefined
                ? String(t.track_price)
                : '',
          })),
        })),
        has_sales_windows: (initialData.windows || []).length > 0,
        pages: (initialData.pages || []).map((p) => ({
          id: p.id,
          name: p.name || '',
          pdf_url: p.pdf_url || '',
          video_url: p.video_url || '',
          pdf_id: p.pdf_id || null,
          pdf_upload_url: p.pdf_url || null,
          form_id: p.form_id || null,
          waivers: (p.waivers || []).map((waiverId) => ({
            waiver: String(waiverId),
          })),
          order: p.order || 1,
        })),
        pages_with_order: (initialData.pages || []).map((p) => ({
          id: p.id,
          name: p.name || '',
          order: p.order || 1,
        })),
        track_ids: (initialData.tracks || []).map((t) => ({
          value: t.id,
          label: t.name,
        })),
        series_ids: (initialData.series || []).map((s) => ({
          value: s.id,
          label: s.name,
        })),
        event_ids: initialData.event_ids || [],
        for_all_events: initialData.for_all_events || false,
        generate_passes: initialData.generate_passes || false,
        display_events: initialData.display_events || false,
        display_track_series: initialData.display_track_series || false,
        visibility: initialData.visibility || 'visible',
        category_id: initialData.category_id || '',
        limit:
          initialData.limit !== null && initialData.limit !== undefined
            ? String(initialData.limit)
            : '',
        low_ticket:
          initialData.low_ticket !== null &&
          initialData.low_ticket !== undefined
            ? String(initialData.low_ticket)
            : '',
        is_featured: initialData.is_featured || false,
        featured_text: initialData.featured_text || '',
        set_member_number: initialData.set_member_number || false,
        prefix_text: initialData.prefix_text || '',
        suffix_text: initialData.suffix_text || '',
        last_member_number:
          initialData.last_member_number !== null &&
          initialData.last_member_number !== undefined
            ? String(initialData.last_member_number)
            : '',
        status: initialData.status || 'active',
      });
      setDataLoaded(true);
    }
  }, [initialData, dataLoaded]);

  // Focus on name input when mounted (add mode only)
  useEffect(() => {
    if (!isEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditMode]);

  // Get selected track and series IDs
  const selectedTrackIds = (formData.track_ids || []).map((t) =>
    typeof t === 'object' ? parseInt(t.value) : parseInt(t)
  );
  const selectedSeriesIds = (formData.series_ids || []).map((s) =>
    typeof s === 'object' ? parseInt(s.value) : parseInt(s)
  );

  // Helper to convert YYYY-MM-DD to MM-DD-YYYY for GraphQL Date type
  const formatDateForQuery = (dateStr) => {
    if (!dateStr) return null;
    // If already in MM-DD-YYYY format, return as is
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return dateStr;
    // Convert from YYYY-MM-DD to MM-DD-YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return `${month}-${day}-${year}`;
    }
    return dateStr;
  };

  // Fetch events based on account and date range
  const { data: eventsData, loading: eventsLoading } = useQuery(
    GET_EVENTS_BY_DATE_RANGE,
    {
      variables: {
        input: {
          account_id: accountId ? parseInt(accountId) : null,
          start_date: formatDateForQuery(formData.start_date),
          end_date: formatDateForQuery(formData.end_date),
        },
      },
      skip: !accountId || !formData.start_date || !formData.end_date,
    }
  );
  const events = eventsData?.getEventsByDateRange || [];

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // Handle attachment changes for description image
  const handleAttachmentsChange = (attachments) => {
    if (attachments.length > 0) {
      setFormData((prev) => ({
        ...prev,
        description_image_id: parseInt(attachments[0].id) || null,
        description_image_url: attachments[0].url,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        description_image_id: null,
        description_image_url: null,
      }));
    }
  };

  // Convert description_image to attachments array format for preview
  const getAttachmentsFromImage = () => {
    if (!formData.description_image_url) return [];
    return [
      {
        id: formData.description_image_id || 'description_image',
        name: 'Description Image',
        size: 0,
        sizeFormatted: '',
        url: formData.description_image_url,
        type: 'image/*',
        isImage: true,
      },
    ];
  };

  // Tier handlers (ticket-level)
  const addTier = () => {
    const newTier = {
      id: `tier_${Date.now()}`,
      name: '',
      price: '',
      track_price: '',
    };
    setFormData((prev) => ({
      ...prev,
      tiers: [...prev.tiers, newTier],
    }));
  };

  const removeTier = (index) => {
    setFormData((prev) => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index),
    }));
  };

  const updateTier = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      tiers: prev.tiers.map((t, i) =>
        i === index ? { ...t, [field]: value } : t
      ),
    }));
  };

  // Sales window handlers
  const addSalesWindow = () => {
    const newWindow = {
      id: `window_${Date.now()}`,
      name: '',
      ends: '',
      time: '23:59',
      price: '',
      tiers: [],
    };
    setFormData((prev) => ({
      ...prev,
      windows: [...prev.windows, newWindow],
    }));
  };

  const removeSalesWindow = (index) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.filter((_, i) => i !== index),
    }));
  };

  const updateSalesWindow = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((w, i) =>
        i === index ? { ...w, [field]: value } : w
      ),
    }));
  };

  // Window tier handlers
  const addTierToWindow = (windowIndex) => {
    const newTier = {
      id: `tier_${Date.now()}`,
      name: '',
      price: '',
      track_price: '',
    };
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((w, i) =>
        i === windowIndex ? { ...w, tiers: [...w.tiers, newTier] } : w
      ),
    }));
  };

  const removeTierFromWindow = (windowIndex, tierIndex) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((w, i) =>
        i === windowIndex
          ? { ...w, tiers: w.tiers.filter((_, ti) => ti !== tierIndex) }
          : w
      ),
    }));
  };

  const updateWindowTier = (windowIndex, tierIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((w, wi) =>
        wi === windowIndex
          ? {
              ...w,
              tiers: w.tiers.map((t, ti) =>
                ti === tierIndex ? { ...t, [field]: value } : t
              ),
            }
          : w
      ),
    }));
  };

  // Registration Pages handlers
  const getHighestPageOrderNumber = (pages) => {
    if (!pages || pages.length === 0) return 0;
    return Math.max(...pages.map((p) => p.order || 0));
  };

  const addPage = () => {
    const newOrder =
      formData.pages.length > 0
        ? getHighestPageOrderNumber(formData.pages) + 1
        : 1;
    const newPage = {
      id: `temp-page-${Date.now()}`,
      name: '',
      pdf_url: '',
      video_url: '',
      pdf_id: null,
      pdf_upload_url: null,
      form_id: null,
      waivers: [],
      order: newOrder,
    };

    setFormData((prev) => ({
      ...prev,
      pages: [...prev.pages, newPage],
      pages_with_order: [...prev.pages_with_order, newPage],
    }));
  };

  const removePage = (index) => {
    const pageToRemove = formData.pages[index];
    setFormData((prev) => ({
      ...prev,
      pages: prev.pages.filter((_, i) => i !== index),
      pages_with_order: prev.pages_with_order.filter((p) =>
        p.id ? p.id !== pageToRemove.id : p.order !== pageToRemove.order
      ),
    }));
  };

  const updatePage = (index, field, value) => {
    setFormData((prev) => {
      const updatedPages = prev.pages.map((page, i) =>
        i === index ? { ...page, [field]: value } : page
      );
      // Also update pages_with_order to keep them in sync
      const pageToUpdate = prev.pages[index];
      const updatedPagesWithOrder = prev.pages_with_order.map((p) =>
        (p.id && p.id === pageToUpdate.id) ||
        (!p.id && p.order === pageToUpdate.order)
          ? { ...p, [field]: value }
          : p
      );
      return {
        ...prev,
        pages: updatedPages,
        pages_with_order: updatedPagesWithOrder,
      };
    });
  };

  const addWaiverToPage = (pageIndex) => {
    setFormData((prev) => ({
      ...prev,
      pages: prev.pages.map((page, i) =>
        i === pageIndex
          ? { ...page, waivers: [...(page.waivers || []), { waiver: '' }] }
          : page
      ),
    }));
  };

  const removeWaiverFromPage = (pageIndex, waiverIndex) => {
    setFormData((prev) => ({
      ...prev,
      pages: prev.pages.map((page, i) =>
        i === pageIndex
          ? {
              ...page,
              waivers: page.waivers.filter((_, wi) => wi !== waiverIndex),
            }
          : page
      ),
    }));
  };

  const updateWaiverInPage = (pageIndex, waiverIndex, value) => {
    setFormData((prev) => ({
      ...prev,
      pages: prev.pages.map((page, i) =>
        i === pageIndex
          ? {
              ...page,
              waivers: page.waivers.map((w, wi) =>
                wi === waiverIndex ? { waiver: value } : w
              ),
            }
          : page
      ),
    }));
  };

  const handlePagesSort = (sortedPages) => {
    // Update order based on new position
    const reorderedPages = sortedPages.map((page, index) => ({
      ...page,
      order: index + 1,
    }));

    setFormData((prev) => ({
      ...prev,
      pages_with_order: reorderedPages,
      // Also update the main pages array order
      pages: prev.pages.map((page) => {
        const sortedPage = reorderedPages.find((p) => p.id === page.id);
        return sortedPage ? { ...page, order: sortedPage.order } : page;
      }),
    }));
  };

  const getOrderedItems = (items) => {
    return [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  // Event selection helpers
  const isEventSelected = (eventId) => {
    return (formData.event_ids || []).some(
      (id) => parseInt(id) === parseInt(eventId)
    );
  };

  const toggleEvent = (eventId) => {
    const currentIds = formData.event_ids || [];
    const eventIdInt = parseInt(eventId);

    if (isEventSelected(eventId)) {
      setFormData((prev) => ({
        ...prev,
        event_ids: currentIds.filter((id) => parseInt(id) !== eventIdInt),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        event_ids: [...currentIds, eventIdInt],
      }));
    }
  };

  const selectAllEvents = () => {
    setFormData((prev) => ({
      ...prev,
      event_ids: events.map((e) => parseInt(e.id)),
    }));
  };

  const deselectAllEvents = () => {
    setFormData((prev) => ({
      ...prev,
      event_ids: [],
    }));
  };

  const allEventsSelected =
    events.length > 0 && events.every((e) => isEventSelected(e.id));

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Validation
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.name.trim()) {
        newErrors.name = 'Ticket name is required';
      }
      if (!formData.start_date) {
        newErrors.start_date = 'Start date is required';
      }
      if (!formData.end_date) {
        newErrors.end_date = 'End date is required';
      }
    }

    if (step === 2) {
      if (!formData.price && formData.price !== 0) {
        newErrors.price = 'Price is required';
      } else if (
        isNaN(parseFloat(formData.price)) ||
        parseFloat(formData.price) < 0
      ) {
        newErrors.price = 'Price must be a valid positive number';
      }

      if (!formData.fee_dollar && formData.fee_dollar !== 0) {
        newErrors.fee_dollar = 'Fee dollar is required';
      } else if (
        isNaN(parseFloat(formData.fee_dollar)) ||
        parseFloat(formData.fee_dollar) < 0
      ) {
        newErrors.fee_dollar = 'Fee dollar must be a valid positive number';
      }

      if (!formData.fee_percent && formData.fee_percent !== 0) {
        newErrors.fee_percent = 'Fee percent is required';
      } else if (
        isNaN(parseFloat(formData.fee_percent)) ||
        parseFloat(formData.fee_percent) < 0
      ) {
        newErrors.fee_percent = 'Fee percent must be a valid positive number';
      }
    }

    if (step === 5) {
      formData.pages.forEach((page, index) => {
        if (!page.name?.trim()) {
          newErrors[`page_name_${index}`] = 'Page name is required';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const goToStep = (step) => {
    // In edit mode, allow jumping to any step
    if (isEditMode) {
      setCurrentStep(step);
      return;
    }
    // In add mode, only allow going back or forward one step with validation
    if (step < currentStep) {
      setCurrentStep(step);
    } else if (step === currentStep + 1 && validateStep(currentStep)) {
      setCurrentStep(step);
    }
  };

  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'active';
    return 'pending';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Ticket name is required';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }
    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (!formData.price && formData.price !== 0) {
      newErrors.price = 'Price is required';
    } else if (
      isNaN(parseFloat(formData.price)) ||
      parseFloat(formData.price) < 0
    ) {
      newErrors.price = 'Price must be a valid positive number';
    }

    formData.pages.forEach((page, index) => {
      if (!page.name?.trim()) {
        newErrors[`page_name_${index}`] = 'Page name is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // Helper to check if an ID is a real database ID (integer) vs a temp ID (string like "tier_123")
    const isRealId = (id) => {
      if (!id) return false;
      if (typeof id === 'number') return true;
      if (typeof id === 'string' && /^\d+$/.test(id)) return true;
      return false;
    };

    // Process tiers for TicketTierInputV2
    const processedTiers = formData.tiers
      .filter((t) => t.name.trim())
      .map((t) => {
        const tier = {
          name: t.name.trim(),
          price: t.price ? parseFloat(t.price) : 0,
          track_price: t.track_price ? parseFloat(t.track_price) : null,
        };
        // Include id only if it's a real database ID (for updates)
        if (isRealId(t.id)) {
          tier.id = parseInt(t.id);
        }
        return tier;
      });

    // Process windows for TicketWindowInput
    const processedWindows = formData.windows
      .filter((w) => w.name.trim() && w.ends)
      .map((w) => {
        const window = {
          name: w.name.trim(),
          ends: formatDateForQuery(w.ends),
          time: w.time || '23:59',
          price: w.price ? parseFloat(w.price) : null,
          tiers: (w.tiers || [])
            .filter((t) => t.name.trim())
            .map((t) => {
              const tier = {
                name: t.name.trim(),
                price: t.price ? parseFloat(t.price) : 0,
                track_price: t.track_price ? parseFloat(t.track_price) : null,
              };
              if (isRealId(t.id)) {
                tier.id = parseInt(t.id);
              }
              return tier;
            }),
        };
        // Include id only if it's a real database ID (for updates)
        if (isRealId(w.id)) {
          window.id = parseInt(w.id);
        }
        return window;
      });

    // Process pages for AnnualPageInput
    const processedPages = formData.pages
      .filter((p) => p.name?.trim())
      .map((p) => {
        const page = {
          name: p.name.trim(),
          order: p.order || 1,
          pdf_url: p.pdf_url || null,
          pdf_id: p.pdf_id ? parseInt(p.pdf_id) : null,
          video_url: p.video_url || null,
          form_id: p.form_id || null,
          waivers: (p.waivers || [])
            .filter((w) => w.waiver)
            .map((w) => parseInt(w.waiver)),
        };
        // Include id only if it's a real database ID (for updates)
        if (isRealId(p.id)) {
          page.id = parseInt(p.id);
        }
        return page;
      });

    const submitData = {
      name: formData.name.trim(),
      ticket_type: formData.ticket_type,
      description: formData.description.trim() || null,
      description_image_id: formData.description_image_id || null,
      description_video: formData.description_video.trim() || null,
      color_code: formData.color_code || '',
      price: parseFloat(formData.price) || 0,
      track_price: formData.track_price
        ? parseFloat(formData.track_price)
        : null,
      fee_dollar: formData.fee_dollar ? parseFloat(formData.fee_dollar) : null,
      fee_percent: formData.fee_percent
        ? parseFloat(formData.fee_percent)
        : null,
      start_date: formatDateForQuery(formData.start_date),
      end_date: formatDateForQuery(formData.end_date),
      sale_start: formatDateForQuery(formData.sale_start),
      on_sale_time: formData.on_sale_time || null,
      sale_end: formatDateForQuery(formData.sale_end),
      off_sale_time: formData.off_sale_time || null,
      show_fee: formData.show_fee,
      international_fee: formData.international_fee,
      marketing_fee: formData.marketing_fee,
      reserved_seat: formData.reserved_seat,
      reserved_seat_category: formData.reserved_seat_category.trim() || null,
      reserved_seat_prefix: formData.reserved_seat_prefix.trim() || null,
      reserved_seat_section: formData.reserved_seat_section,
      track_ids: selectedTrackIds.length > 0 ? selectedTrackIds : null,
      series_ids: selectedSeriesIds.length > 0 ? selectedSeriesIds : null,
      event_ids: formData.for_all_events
        ? null
        : formData.event_ids.length > 0
        ? formData.event_ids
        : null,
      for_all_events: formData.for_all_events,
      generate_passes: formData.generate_passes,
      display_events: formData.display_events,
      display_track_series: formData.display_track_series,
      visibility: formData.visibility,
      category_id: formData.category_id ? parseInt(formData.category_id) : null,
      limit: formData.limit ? parseInt(formData.limit) : null,
      low_ticket: formData.low_ticket ? parseInt(formData.low_ticket) : null,
      is_featured: formData.is_featured,
      featured_text: formData.featured_text || null,
      set_member_number: formData.set_member_number,
      prefix_text: formData.prefix_text || null,
      suffix_text: formData.suffix_text || null,
      last_member_number: formData.last_member_number
        ? parseInt(formData.last_member_number)
        : null,
      status: formData.status || 'draft',
      tiers: processedTiers.length > 0 ? processedTiers : null,
      windows: processedWindows.length > 0 ? processedWindows : null,
      pages: processedPages.length > 0 ? processedPages : null,
    };

    try {
      await onSubmit(submitData);
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: isEditMode ? 'Error Updating Ticket' : 'Error Creating Ticket',
        message:
          error.message ||
          `Failed to ${
            isEditMode ? 'update' : 'create'
          } annual ticket. Please try again.`,
      });
    }
  };

  return (
    <PageWrapper>
      <Container>
        <BackButtonWrapper>
          <ButtonV2 type="button" onClick={onCancel} icon={<ArrowLeftIcon />}>
            Back to Annual Tickets
          </ButtonV2>
        </BackButtonWrapper>

        <PageHeader>
          <HeaderLeft>
            <Title>
              {isEditMode ? 'Edit Annual Ticket' : 'Add Annual Ticket'}
            </Title>
            <Subtitle>
              {currentStep === 1 &&
                `${
                  isEditMode ? 'Update' : 'Set up'
                } the basic details for your annual ticket`}
              {currentStep === 2 && 'Configure pricing and fees'}
              {currentStep === 3 && 'Associate with tracks, series, and events'}
              {currentStep === 4 && 'Set additional options and settings'}
              {currentStep === 5 && 'Configure registration pages for checkout'}
            </Subtitle>
          </HeaderLeft>
          <HeaderRight>
            <HeaderButtonWrapper>
              <ButtonV2 type="button" onClick={onCancel}>
                Cancel
              </ButtonV2>
            </HeaderButtonWrapper>
            {isEditMode && (
              <HeaderButtonWrapper>
                <ButtonV2
                  elevated
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </ButtonV2>
              </HeaderButtonWrapper>
            )}
          </HeaderRight>
        </PageHeader>

        {/* Step Indicator */}
        <StepperContainer>
          <StepperTrack>
            {steps.map((step, index) => {
              // In edit mode, all steps are clickable; in add mode, only completed steps
              const isStepClickable = isEditMode
                ? step.number !== currentStep
                : step.number < currentStep;

              return (
                <StepItem
                  key={step.number}
                  isClickable={isStepClickable}
                  onClick={() => isStepClickable && goToStep(step.number)}
                >
                  <StepCircle
                    type="button"
                    status={getStepStatus(step.number)}
                    isClickable={isStepClickable}
                  >
                    {getStepStatus(step.number) === 'completed' ? (
                      <CheckIcon />
                    ) : (
                      step.number
                    )}
                  </StepCircle>
                  <StepLabel status={getStepStatus(step.number)}>
                    {step.label}
                  </StepLabel>
                  {index < steps.length - 1 && (
                    <StepConnector completed={step.number < currentStep} />
                  )}
                </StepItem>
              );
            })}
          </StepperTrack>
        </StepperContainer>

        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <FormCard>
            <FormSection>
              <SectionTitle>Basic Information</SectionTitle>

              <FormRow columns="2fr 1fr">
                <Input
                  ref={inputRef}
                  label="Ticket Name"
                  required
                  type="text"
                  placeholder="e.g., Season Pass 2025"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                />

                <Select
                  label="Type"
                  value={formData.ticket_type}
                  onChange={(e) => handleChange('ticket_type', e.target.value)}
                  options={ANNUAL_TICKET_TYPES}
                />
              </FormRow>

              <Textarea
                label="Description"
                placeholder="Enter ticket description..."
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />

              <FormRow columns="1fr 1fr" style={{ marginTop: '16px' }}>
                <FormGroup>
                  <Label>Description Image</Label>
                  <AttachmentUpload
                    attachments={getAttachmentsFromImage()}
                    onAttachmentsChange={handleAttachmentsChange}
                    maxFiles={1}
                    maxFileSize={5242880}
                    channel="SMS"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Description URL (Video)</Label>
                  <Input
                    type="url"
                    placeholder="e.g., https://vimeo.com/... or https://youtube.com/..."
                    value={formData.description_video}
                    onChange={(e) =>
                      handleChange('description_video', e.target.value)
                    }
                  />
                </FormGroup>
              </FormRow>

              {/* Dates Section */}
              <SectionTitle style={{ marginTop: '24px' }}>
                Validity Period
              </SectionTitle>
              <SectionDescription>
                When is this ticket valid for use?
              </SectionDescription>

              <FormRow columns="1fr 1fr">
                <FormGroup>
                  <Label>
                    Start Date <span style={{ color: '#e53e3e' }}>*</span>
                  </Label>
                  <DateTimePicker
                    value={formData.start_date}
                    onChange={(date) => handleChange('start_date', date)}
                    placeholder="Select start date"
                    showTime={false}
                  />
                  {errors.start_date && (
                    <ErrorText>{errors.start_date}</ErrorText>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>
                    End Date <span style={{ color: '#e53e3e' }}>*</span>
                  </Label>
                  <DateTimePicker
                    value={formData.end_date}
                    onChange={(date) => handleChange('end_date', date)}
                    placeholder="Select end date"
                    showTime={false}
                  />
                  {errors.end_date && <ErrorText>{errors.end_date}</ErrorText>}
                </FormGroup>
              </FormRow>

              {/* Ticket Type Toggles */}
              <TwoColumnGrid style={{ marginTop: '24px' }}>
                <ToggleCard>
                  <ToggleInfo>
                    <ToggleTitle>Reserved Seating</ToggleTitle>
                    <ToggleDescription>
                      Enable Seats.io integration for seat selection
                    </ToggleDescription>
                  </ToggleInfo>
                  <Toggle
                    checked={formData.reserved_seat}
                    onChange={(e) =>
                      handleChange('reserved_seat', e.target.checked)
                    }
                  />
                </ToggleCard>

                <ToggleCard>
                  <ToggleInfo>
                    <ToggleTitle>Sales Windows</ToggleTitle>
                    <ToggleDescription>
                      Create time-based pricing periods (e.g., Early Bird)
                    </ToggleDescription>
                  </ToggleInfo>
                  <Toggle
                    checked={formData.has_sales_windows}
                    onChange={(e) =>
                      handleChange('has_sales_windows', e.target.checked)
                    }
                  />
                </ToggleCard>
              </TwoColumnGrid>

              {/* Step Navigation */}
              <StepNavigation>
                <div />
                <NavButtonWrapper>
                  <ButtonV2 elevated type="button" onClick={handleNextStep}>
                    Continue to Pricing
                    <ArrowRightIcon />
                  </ButtonV2>
                </NavButtonWrapper>
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}

        {/* Step 2: Pricing */}
        {currentStep === 2 && (
          <FormCard>
            <FormSection>
              <SectionTitle>Pricing</SectionTitle>

              <FormRow columns="1fr 1fr">
                <Input
                  label="Price"
                  required
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  error={!!errors.price}
                  helperText={errors.price}
                />

                <Input
                  label="At Track Price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.track_price}
                  onChange={(e) => handleChange('track_price', e.target.value)}
                />
              </FormRow>

              {/* Fees */}
              <SectionTitle style={{ marginTop: '24px' }}>Fees</SectionTitle>
              <FormRow columns="1fr 1fr">
                <Input
                  label="Fee Dollar"
                  required
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.fee_dollar}
                  onChange={(e) => handleChange('fee_dollar', e.target.value)}
                  error={!!errors.fee_dollar}
                  helperText={errors.fee_dollar}
                />

                <FormGroup>
                  <LabelWithTooltip>
                    Fee Percent <span style={{ color: '#ef4444' }}>*</span>
                    <Tooltip
                      info
                      content="This percentage needs to include the 3.25% base processing fee."
                      iconColor={infoIconColor}
                      iconSize="16px"
                    />
                  </LabelWithTooltip>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.fee_percent}
                    onChange={(e) =>
                      handleChange('fee_percent', e.target.value)
                    }
                    error={!!errors.fee_percent}
                    helperText={errors.fee_percent}
                  />
                </FormGroup>
              </FormRow>

              {/* Fee Options - Annual Specific */}
              <SectionTitle style={{ marginTop: '24px' }}>
                Fee Options
              </SectionTitle>

              <ToggleRow>
                <ToggleInfo>
                  <ToggleTitle>Promoter Absorbs Fees</ToggleTitle>
                  <ToggleDescription>
                    Hide fees from customer (promoter pays)
                  </ToggleDescription>
                </ToggleInfo>
                <Toggle
                  checked={!formData.show_fee}
                  onChange={(e) => {
                    const absorbing = e.target.checked;
                    setFormData((prev) => ({
                      ...prev,
                      show_fee: !absorbing,
                      // Turn off international and marketing fees when absorbing
                      ...(absorbing && {
                        international_fee: false,
                        marketing_fee: false,
                      }),
                    }));
                  }}
                />
              </ToggleRow>

              <ToggleRow>
                <ToggleInfo>
                  <ToggleTitle>Collect International Fees</ToggleTitle>
                  <ToggleDescription>
                    Additional fee for international transactions
                  </ToggleDescription>
                </ToggleInfo>
                <Toggle
                  checked={formData.international_fee}
                  onChange={(e) =>
                    handleChange('international_fee', e.target.checked)
                  }
                />
              </ToggleRow>

              <ToggleRow>
                <ToggleInfo>
                  <ToggleTitle>Collect Marketing Fees</ToggleTitle>
                  <ToggleDescription>
                    Additional marketing/promotion fee
                  </ToggleDescription>
                </ToggleInfo>
                <Toggle
                  checked={formData.marketing_fee}
                  onChange={(e) =>
                    handleChange('marketing_fee', e.target.checked)
                  }
                />
              </ToggleRow>

              {/* Sale Period - Only show when Sales Windows is disabled */}
              {!formData.has_sales_windows && (
                <>
                  <SectionTitle style={{ marginTop: '24px' }}>
                    Sale Period
                  </SectionTitle>
                  <SectionDescription>
                    When should this ticket be available for purchase?
                  </SectionDescription>

                  <FormRow columns="1fr 1fr">
                    <FormGroup>
                      <Label>Sale Start Date</Label>
                      <DateTimePicker
                        value={formData.sale_start}
                        onChange={(date) => handleChange('sale_start', date)}
                        placeholder="Select start date"
                        showTime={false}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>On Sale Time</Label>
                      <DateTimePicker
                        value={formData.on_sale_time || '00:00'}
                        onChange={(time) => handleChange('on_sale_time', time)}
                        timeOnly
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow columns="1fr 1fr">
                    <FormGroup>
                      <Label>Sale End Date</Label>
                      <DateTimePicker
                        value={formData.sale_end}
                        onChange={(date) => handleChange('sale_end', date)}
                        placeholder="Select end date"
                        showTime={false}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Off Sale Time</Label>
                      <DateTimePicker
                        value={formData.off_sale_time || '23:59'}
                        onChange={(time) => handleChange('off_sale_time', time)}
                        timeOnly
                      />
                    </FormGroup>
                  </FormRow>
                </>
              )}

              {/* Tiered Pricing - For reserved seats (coexists with sales window tiers) */}
              {formData.reserved_seat && (
                <TierSection>
                  <TierHeader>
                    <TierTitle>Tiered Pricing</TierTitle>
                    <AddTierButtonWrapper>
                      <ButtonV2
                        elevated
                        type="button"
                        onClick={addTier}
                        icon={<PlusIcon />}
                      >
                        Add
                      </ButtonV2>
                    </AddTierButtonWrapper>
                  </TierHeader>

                  {formData.tiers.length === 0 ? (
                    <EmptyTiersMessage>
                      No pricing tiers configured. Add tiers for different seat
                      categories.
                    </EmptyTiersMessage>
                  ) : (
                    <>
                      <TierRow>
                        <Label>Label (Name)</Label>
                        <Label>App Price</Label>
                        <Label>Track Price</Label>
                        <div />
                      </TierRow>
                      {formData.tiers.map((tier, index) => (
                        <TierRow key={tier.id}>
                          <Input
                            type="text"
                            placeholder="e.g., Front Row"
                            value={tier.name}
                            onChange={(e) =>
                              updateTier(index, 'name', e.target.value)
                            }
                          />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={tier.price}
                            onChange={(e) =>
                              updateTier(index, 'price', e.target.value)
                            }
                          />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={tier.track_price}
                            onChange={(e) =>
                              updateTier(index, 'track_price', e.target.value)
                            }
                          />
                          <RemoveTierButtonWrapper>
                            <ButtonV2
                              danger
                              variant="icon"
                              type="button"
                              onClick={() => removeTier(index)}
                              title="Remove tier"
                              icon={<XIcon />}
                            />
                          </RemoveTierButtonWrapper>
                        </TierRow>
                      ))}
                    </>
                  )}
                </TierSection>
              )}

              {/* Sales Windows */}
              {formData.has_sales_windows && (
                <>
                  <SectionTitle style={{ marginTop: '24px' }}>
                    Sales Windows
                  </SectionTitle>
                  <SectionDescription>
                    Create promotional pricing periods with different prices for
                    each time window.
                  </SectionDescription>

                  <SalesWindowsContainer>
                    {formData.windows.length === 0 ? (
                      <EmptyWindowsMessage>
                        No sales windows configured. Add one to enable
                        time-based pricing.
                      </EmptyWindowsMessage>
                    ) : (
                      formData.windows.map((window, windowIndex) => (
                        <SalesWindowCard key={window.id}>
                          <SalesWindowHeader>
                            <SalesWindowTitle>
                              {window.name || `Window ${windowIndex + 1}`}
                            </SalesWindowTitle>
                            <SmallIconButtonWrapper>
                              <ButtonV2
                                danger
                                variant="icon"
                                type="button"
                                onClick={() => removeSalesWindow(windowIndex)}
                                title="Remove window"
                                icon={<XIcon />}
                              />
                            </SmallIconButtonWrapper>
                          </SalesWindowHeader>

                          <FormRow columns="1.5fr 1fr 130px 120px">
                            <FormGroup>
                              <Label>Window Name</Label>
                              <Input
                                type="text"
                                placeholder="e.g., Early Bird"
                                value={window.name}
                                onChange={(e) =>
                                  updateSalesWindow(
                                    windowIndex,
                                    'name',
                                    e.target.value
                                  )
                                }
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label>End Date</Label>
                              <DateTimePicker
                                value={window.ends}
                                onChange={(date) =>
                                  updateSalesWindow(windowIndex, 'ends', date)
                                }
                                placeholder="Select end date"
                                showTime={false}
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label>End Time</Label>
                              <DateTimePicker
                                value={window.time || '23:59'}
                                onChange={(time) =>
                                  updateSalesWindow(windowIndex, 'time', time)
                                }
                                timeOnly
                              />
                            </FormGroup>

                            {/* Show price field if not reserved OR if reserved but no tiers yet */}
                            {(!formData.reserved_seat ||
                              window.tiers.length === 0) && (
                              <FormGroup>
                                <Label>Price</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={window.price}
                                  onChange={(e) =>
                                    updateSalesWindow(
                                      windowIndex,
                                      'price',
                                      e.target.value
                                    )
                                  }
                                />
                              </FormGroup>
                            )}
                          </FormRow>

                          {/* Tiers inside window - only for reserved seats */}
                          {formData.reserved_seat && (
                            <TierSection style={{ marginTop: '16px' }}>
                              <TierHeader>
                                <TierTitle style={{ fontSize: '13px' }}>
                                  Pricing Tiers
                                </TierTitle>
                                <AddTierButtonWrapper>
                                  <ButtonV2
                                    elevated
                                    type="button"
                                    onClick={() => addTierToWindow(windowIndex)}
                                    icon={<PlusIcon />}
                                  >
                                    Add
                                  </ButtonV2>
                                </AddTierButtonWrapper>
                              </TierHeader>

                              {window.tiers.length === 0 ? (
                                <EmptyTiersMessage>
                                  No tiers. Add tiers for seat-based pricing.
                                </EmptyTiersMessage>
                              ) : (
                                <>
                                  <TierRow>
                                    <Label>Label</Label>
                                    <Label>App Price</Label>
                                    <Label>Track Price</Label>
                                    <div />
                                  </TierRow>
                                  {window.tiers.map((tier, tierIndex) => (
                                    <TierRow key={tier.id}>
                                      <Input
                                        type="text"
                                        placeholder="e.g., Front Row"
                                        value={tier.name}
                                        onChange={(e) =>
                                          updateWindowTier(
                                            windowIndex,
                                            tierIndex,
                                            'name',
                                            e.target.value
                                          )
                                        }
                                      />
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        value={tier.price}
                                        onChange={(e) =>
                                          updateWindowTier(
                                            windowIndex,
                                            tierIndex,
                                            'price',
                                            e.target.value
                                          )
                                        }
                                      />
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        value={tier.track_price}
                                        onChange={(e) =>
                                          updateWindowTier(
                                            windowIndex,
                                            tierIndex,
                                            'track_price',
                                            e.target.value
                                          )
                                        }
                                      />
                                      <RemoveTierButtonWrapper>
                                        <ButtonV2
                                          danger
                                          variant="icon"
                                          type="button"
                                          onClick={() =>
                                            removeTierFromWindow(
                                              windowIndex,
                                              tierIndex
                                            )
                                          }
                                          title="Remove tier"
                                          icon={<XIcon />}
                                        />
                                      </RemoveTierButtonWrapper>
                                    </TierRow>
                                  ))}
                                </>
                              )}
                            </TierSection>
                          )}
                        </SalesWindowCard>
                      ))
                    )}

                    <AddWindowButtonWrapper>
                      <ButtonV2
                        elevated
                        type="button"
                        onClick={addSalesWindow}
                        icon={<PlusIcon />}
                      >
                        Add Sales Window
                      </ButtonV2>
                    </AddWindowButtonWrapper>
                  </SalesWindowsContainer>
                </>
              )}

              {/* Step Navigation */}
              <StepNavigation>
                <NavButtonWrapper>
                  <ButtonV2 type="button" onClick={handlePrevStep}>
                    <ArrowLeftIcon />
                    Back
                  </ButtonV2>
                </NavButtonWrapper>
                <NavButtonWrapper>
                  <ButtonV2 elevated type="button" onClick={handleNextStep}>
                    Continue to Associations
                    <ArrowRightIcon />
                  </ButtonV2>
                </NavButtonWrapper>
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}

        {/* Step 3: Associations */}
        {currentStep === 3 && (
          <FormCard>
            <FormSection>
              <SectionTitle>Track & Series Associations</SectionTitle>
              <SectionDescription>
                Select which tracks and series this ticket will be available for
              </SectionDescription>

              <FormRow columns="1fr 1fr">
                <MultiSelect
                  label="Tracks"
                  placeholder="Search tracks..."
                  value={formData.track_ids || []}
                  onChange={(tracks) =>
                    setFormData((prev) => ({
                      ...prev,
                      track_ids: tracks || [],
                    }))
                  }
                  options={tracks.map((track) => ({
                    value: track.id,
                    label: track.name,
                  }))}
                  loading={tracksLoading}
                />

                <MultiSelect
                  label="Series"
                  placeholder="Search series..."
                  value={formData.series_ids || []}
                  onChange={(seriesItems) =>
                    setFormData((prev) => ({
                      ...prev,
                      series_ids: seriesItems || [],
                    }))
                  }
                  options={series.map((s) => ({
                    value: s.id,
                    label: s.name,
                  }))}
                  loading={seriesLoading}
                />
              </FormRow>

              {/* Event Coverage */}
              <SectionTitle style={{ marginTop: '24px' }}>
                Event Coverage
              </SectionTitle>

              <ToggleRow>
                <ToggleInfo>
                  <ToggleTitle>Good for All Events</ToggleTitle>
                  <ToggleDescription>
                    This ticket is valid for all events under the selected
                    tracks/series
                  </ToggleDescription>
                </ToggleInfo>
                <Toggle
                  checked={formData.for_all_events}
                  onChange={(e) =>
                    handleChange('for_all_events', e.target.checked)
                  }
                />
              </ToggleRow>

              {/* Event Selection - Only when not "for all events" */}
              {!formData.for_all_events && (
                <>
                  <SectionTitle style={{ marginTop: '24px' }}>
                    Select Events
                  </SectionTitle>
                  <SectionDescription>
                    Choose specific events this ticket will be valid for
                  </SectionDescription>

                  {!accountId || !formData.start_date || !formData.end_date ? (
                    <EmptyMessage>
                      Set the validity period (start and end dates) in Step 1 to
                      see available events
                    </EmptyMessage>
                  ) : eventsLoading ? (
                    <Loading />
                  ) : events.length === 0 ? (
                    <EmptyMessage>
                      No events found within the selected date range
                    </EmptyMessage>
                  ) : (
                    <>
                      <SelectAllRow>
                        <SelectAllLabel>
                          {formData.event_ids?.length || 0} of {events.length}{' '}
                          events selected
                        </SelectAllLabel>
                        {allEventsSelected ? (
                          <SelectButton
                            type="button"
                            onClick={deselectAllEvents}
                          >
                            Deselect All
                          </SelectButton>
                        ) : (
                          <SelectButton type="button" onClick={selectAllEvents}>
                            Select All
                          </SelectButton>
                        )}
                      </SelectAllRow>

                      <EventsContainer>
                        {events.map((event) => {
                          const selected = isEventSelected(event.id);
                          return (
                            <EventItem
                              key={event.id}
                              $selected={selected}
                              onClick={() => toggleEvent(event.id)}
                            >
                              <EventCheckbox $checked={selected}>
                                {selected && <EventCheckIcon>✓</EventCheckIcon>}
                              </EventCheckbox>
                              <EventDateBadge>
                                <EventDateText>
                                  {event.start_date
                                    ? formatDate(event.start_date)
                                    : 'No date'}
                                </EventDateText>
                              </EventDateBadge>
                              <EventName $selected={selected}>
                                {event.name}
                              </EventName>
                            </EventItem>
                          );
                        })}
                      </EventsContainer>
                    </>
                  )}
                </>
              )}

              {/* Step Navigation */}
              <StepNavigation>
                <NavButtonWrapper>
                  <ButtonV2 type="button" onClick={handlePrevStep}>
                    <ArrowLeftIcon />
                    Back
                  </ButtonV2>
                </NavButtonWrapper>
                <NavButtonWrapper>
                  <ButtonV2 elevated type="button" onClick={handleNextStep}>
                    Continue to Options
                    <ArrowRightIcon />
                  </ButtonV2>
                </NavButtonWrapper>
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}

        {/* Step 4: Options */}
        {currentStep === 4 && (
          <FormCard>
            <FormSection>
              <SectionTitle>Visibility & Limits</SectionTitle>

              <FormRow columns="1fr 1fr 1fr">
                <Select
                  label="Status"
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  options={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'published', label: 'Published' },
                  ]}
                />

                <Select
                  label="Visibility"
                  value={formData.visibility}
                  onChange={(e) => handleChange('visibility', e.target.value)}
                  options={VISIBILITY_OPTIONS}
                />

                <CreatableSelect
                  label="Category"
                  value={formData.category_id}
                  onChange={(option) =>
                    handleChange('category_id', option?.value || '')
                  }
                  onCreate={handleCreateCategory}
                  placeholder="Select or create a category..."
                  options={categoryOptions}
                />

                <Input
                  label="Ticket Limit"
                  type="number"
                  min="0"
                  placeholder="Unlimited"
                  value={formData.limit}
                  onChange={(e) => handleChange('limit', e.target.value)}
                />
              </FormRow>

              <FormRow columns="1fr 1fr 1fr">
                <ColorSelect
                  label="Color Code"
                  value={formData.color_code}
                  onChange={(value) => handleChange('color_code', value)}
                  options={COLOR_CODES}
                  placeholder="Select a color..."
                  allowEmpty
                  emptyLabel="No Color"
                />
                <div />
                <div />
              </FormRow>

              {/* Additional Options */}
              <SectionTitle style={{ marginTop: '24px' }}>
                Additional Options
              </SectionTitle>

              <ToggleRow>
                <ToggleInfo>
                  <ToggleTitle>Generate Pass for Each Event</ToggleTitle>
                  <ToggleDescription>
                    Create individual passes when events are created
                  </ToggleDescription>
                </ToggleInfo>
                <Toggle
                  checked={formData.generate_passes}
                  onChange={(e) =>
                    handleChange('generate_passes', e.target.checked)
                  }
                />
              </ToggleRow>

              <ToggleRow>
                <ToggleInfo>
                  <ToggleTitle>Featured Ticket</ToggleTitle>
                  <ToggleDescription>
                    Highlight this ticket with special styling
                  </ToggleDescription>
                </ToggleInfo>
                <Toggle
                  checked={formData.is_featured}
                  onChange={(e) =>
                    handleChange('is_featured', e.target.checked)
                  }
                />
              </ToggleRow>

              {formData.is_featured && (
                <FormGroup style={{ marginTop: '16px', marginBottom: '8px' }}>
                  <Label>Featured Text</Label>
                  <Input
                    type="text"
                    placeholder="e.g., Best Value!"
                    value={formData.featured_text}
                    onChange={(e) =>
                      handleChange('featured_text', e.target.value)
                    }
                  />
                </FormGroup>
              )}

              {formData.ticket_type === 'membership' && (
                <>
                  <ToggleRow>
                    <ToggleInfo>
                      <ToggleTitle>Assign Member Numbers</ToggleTitle>
                      <ToggleDescription>
                        Automatically assign member numbers to ticket purchasers
                      </ToggleDescription>
                    </ToggleInfo>
                    <Toggle
                      checked={formData.set_member_number}
                      onChange={(e) =>
                        handleChange('set_member_number', e.target.checked)
                      }
                    />
                  </ToggleRow>

                  {formData.set_member_number && (
                    <FormRow
                      columns="1fr 1fr 1fr"
                      style={{ marginTop: '16px' }}
                    >
                      <FormGroup>
                        <Label>Prefix</Label>
                        <Input
                          type="text"
                          placeholder="e.g., MEM-"
                          value={formData.prefix_text}
                          onChange={(e) =>
                            handleChange('prefix_text', e.target.value)
                          }
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Starting Number</Label>
                        <Input
                          type="number"
                          min="1"
                          placeholder="1"
                          value={formData.last_member_number}
                          onChange={(e) =>
                            handleChange('last_member_number', e.target.value)
                          }
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Suffix</Label>
                        <Input
                          type="text"
                          placeholder="e.g., -2025"
                          value={formData.suffix_text}
                          onChange={(e) =>
                            handleChange('suffix_text', e.target.value)
                          }
                        />
                      </FormGroup>
                    </FormRow>
                  )}
                </>
              )}

              {/* Reserved Seat Options - if enabled in step 1 */}
              {formData.reserved_seat && (
                <>
                  <SectionTitle style={{ marginTop: '24px' }}>
                    Reserved Seating Options
                  </SectionTitle>

                  <FormRow columns="1fr 1fr">
                    <FormGroup>
                      <Label>Seat Category</Label>
                      <Input
                        type="text"
                        placeholder="e.g., VIP, Standard"
                        value={formData.reserved_seat_category}
                        onChange={(e) =>
                          handleChange('reserved_seat_category', e.target.value)
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Seat Prefix</Label>
                      <Input
                        type="text"
                        placeholder="e.g., A, B, C"
                        value={formData.reserved_seat_prefix}
                        onChange={(e) =>
                          handleChange('reserved_seat_prefix', e.target.value)
                        }
                      />
                    </FormGroup>
                  </FormRow>

                  <ToggleRow style={{ marginTop: '16px' }}>
                    <ToggleInfo>
                      <ToggleTitle>Section Based</ToggleTitle>
                      <ToggleDescription>
                        Organize seats by section
                      </ToggleDescription>
                    </ToggleInfo>
                    <Toggle
                      checked={formData.reserved_seat_section}
                      onChange={(e) =>
                        handleChange('reserved_seat_section', e.target.checked)
                      }
                    />
                  </ToggleRow>
                </>
              )}

              {/* Step Navigation */}
              <StepNavigation>
                <NavButtonWrapper>
                  <ButtonV2 type="button" onClick={handlePrevStep}>
                    <ArrowLeftIcon />
                    Back
                  </ButtonV2>
                </NavButtonWrapper>
                <NavButtonWrapper>
                  <ButtonV2 elevated type="button" onClick={handleNextStep}>
                    Continue to Pages
                    <ArrowRightIcon />
                  </ButtonV2>
                </NavButtonWrapper>
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}

        {/* Step 5: Pages */}
        {currentStep === 5 && (
          <FormCard>
            <FormSection>
              <SectionTitle>Registration Pages</SectionTitle>
              <SectionDescription>
                Add pages that will be displayed during checkout. Pages can
                include PDFs, videos, forms, and waivers.
              </SectionDescription>

              <PagesContainer>
                <PagesListSection>
                  {formData.pages.length === 0 ? (
                    <EmptyPagesMessage>
                      No pages configured yet. Add a page to get started.
                    </EmptyPagesMessage>
                  ) : (
                    formData.pages.map((page, pageIndex) => (
                      <PageCard key={page.id}>
                        <PageCardHeader>
                          <PageCardTitle>
                            {page.name || `Page ${pageIndex + 1}`}
                          </PageCardTitle>
                          <DeletePageButtonWrapper>
                            <ButtonV2
                              danger
                              variant="icon"
                              type="button"
                              onClick={() => removePage(pageIndex)}
                              title="Remove page"
                              icon={<TrashIcon />}
                            />
                          </DeletePageButtonWrapper>
                        </PageCardHeader>

                        <FormRow columns="1fr">
                          <FormGroup>
                            <Label>Page Name *</Label>
                            <Input
                              type="text"
                              placeholder="Enter page name"
                              value={page.name || ''}
                              onChange={(e) =>
                                updatePage(pageIndex, 'name', e.target.value)
                              }
                            />
                            {errors[`page_name_${pageIndex}`] && (
                              <ErrorText>
                                {errors[`page_name_${pageIndex}`]}
                              </ErrorText>
                            )}
                          </FormGroup>
                        </FormRow>

                        <FormRow columns="1fr 1fr">
                          <FormGroup>
                            <Label>PDF URL</Label>
                            <Input
                              type="url"
                              placeholder="https://..."
                              value={page.pdf_url || ''}
                              onChange={(e) =>
                                updatePage(pageIndex, 'pdf_url', e.target.value)
                              }
                            />
                          </FormGroup>

                          <FormGroup>
                            <Label>Video URL</Label>
                            <Input
                              type="url"
                              placeholder="https://..."
                              value={page.video_url || ''}
                              onChange={(e) =>
                                updatePage(
                                  pageIndex,
                                  'video_url',
                                  e.target.value
                                )
                              }
                            />
                          </FormGroup>
                        </FormRow>

                        <FormRow columns="1fr 1fr">
                          <FormGroup>
                            <Label>PDF Upload</Label>
                            <AttachmentUpload
                              attachments={
                                page.pdf_id && page.pdf_upload_url
                                  ? [
                                      {
                                        id: page.pdf_id,
                                        name: 'Uploaded PDF',
                                        size: 0,
                                        sizeFormatted: '',
                                        url: page.pdf_upload_url,
                                        type: 'application/pdf',
                                        isPDF: true,
                                      },
                                    ]
                                  : []
                              }
                              onAttachmentsChange={(attachments) => {
                                if (attachments.length > 0) {
                                  updatePage(
                                    pageIndex,
                                    'pdf_id',
                                    attachments[0].id
                                  );
                                  updatePage(
                                    pageIndex,
                                    'pdf_upload_url',
                                    attachments[0].url
                                  );
                                } else {
                                  updatePage(pageIndex, 'pdf_id', null);
                                  updatePage(pageIndex, 'pdf_upload_url', null);
                                }
                              }}
                              maxFiles={1}
                              maxFileSize={10485760}
                              channel="SMS"
                            />
                          </FormGroup>

                          <FormGroup>
                            <Label>Form ID</Label>
                            <SearchableSelect
                              value={
                                page.form_id ? parseInt(page.form_id) : null
                              }
                              onChange={(option) => {
                                updatePage(
                                  pageIndex,
                                  'form_id',
                                  option?.value?.toString() || null
                                );
                              }}
                              options={formstackOptions}
                              loading={formstackLoading}
                              onSearch={handleFormstackSearch}
                              minSearchLength={4}
                              placeholder="Type to search forms..."
                              noResultsText="No forms found"
                            />
                          </FormGroup>
                        </FormRow>

                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label>Waivers</Label>
                          <WaiversContainer>
                            {(page.waivers || []).length === 0 ? (
                              <EmptyText>No waivers added</EmptyText>
                            ) : (
                              page.waivers.map((waiver, waiverIndex) => (
                                <WaiverRow key={waiverIndex}>
                                  <Select
                                    value={waiver.waiver || ''}
                                    onChange={(e) =>
                                      updateWaiverInPage(
                                        pageIndex,
                                        waiverIndex,
                                        e.target.value
                                      )
                                    }
                                    placeholder={
                                      waiversLoading
                                        ? 'Loading waivers...'
                                        : 'Select a waiver...'
                                    }
                                    options={waivers.map((w) => ({
                                      label: w.name,
                                      value: w.id,
                                    }))}
                                    style={{ flex: 1 }}
                                  />
                                  <RemoveWaiverButtonWrapper>
                                    <ButtonV2
                                      danger
                                      variant="icon"
                                      type="button"
                                      onClick={() =>
                                        removeWaiverFromPage(
                                          pageIndex,
                                          waiverIndex
                                        )
                                      }
                                      title="Remove waiver"
                                      icon={<MinusIcon />}
                                    />
                                  </RemoveWaiverButtonWrapper>
                                </WaiverRow>
                              ))
                            )}
                            <AddWaiverButtonWrapper>
                              <ButtonV2
                                elevated
                                type="button"
                                onClick={() => addWaiverToPage(pageIndex)}
                                icon={<PlusIcon />}
                              >
                                Add another waiver
                              </ButtonV2>
                            </AddWaiverButtonWrapper>
                          </WaiversContainer>
                        </FormGroup>
                      </PageCard>
                    ))
                  )}

                  <AddPageButtonWrapper>
                    <ButtonV2
                      elevated
                      type="button"
                      onClick={addPage}
                      icon={<PlusIcon />}
                    >
                      {formData.pages.length > 0
                        ? 'ADD ANOTHER PAGE'
                        : 'ADD PAGE'}
                    </ButtonV2>
                  </AddPageButtonWrapper>
                </PagesListSection>

                {formData.pages_with_order.length > 0 && (
                  <PagesOrderSection>
                    <OrderSectionTitle>Pages Order In App</OrderSectionTitle>
                    <ReactSortable
                      list={getOrderedItems(formData.pages_with_order)}
                      setList={handlePagesSort}
                    >
                      {getOrderedItems(formData.pages_with_order).map(
                        (page) => (
                          <DraggablePageItem key={page.id || page.order}>
                            <DragHandle>
                              <DragHandleIcon />
                            </DragHandle>
                            <PageOrderName>
                              {page.name || 'Unnamed Page'}
                            </PageOrderName>
                          </DraggablePageItem>
                        )
                      )}
                    </ReactSortable>
                  </PagesOrderSection>
                )}
              </PagesContainer>

              {/* Step Navigation */}
              <StepNavigation>
                <NavButtonWrapper>
                  <ButtonV2 type="button" onClick={handlePrevStep}>
                    <ArrowLeftIcon />
                    Back
                  </ButtonV2>
                </NavButtonWrapper>
                {!isEditMode && (
                  <NavButtonWrapper>
                    <ButtonV2
                      elevated
                      type="button"
                      onClick={handleSubmit}
                      disabled={saving}
                    >
                      {saving ? 'Creating...' : 'Create Ticket'}
                      <CheckIcon />
                    </ButtonV2>
                  </NavButtonWrapper>
                )}
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}

        {/* Toaster */}
        <Toaster
          isOpen={toaster.isOpen}
          title={toaster.title}
          message={toaster.message}
          type={toaster.type}
          onClose={() => setToaster({ ...toaster, isOpen: false })}
        />
      </Container>
    </PageWrapper>
  );
};

export default AnnualTicketForm;
